/**
 * ScenarioControls Component
 *
 * Provides sliders for "What-If" scenario analysis in HIP dashboard.
 * Users can adjust Intensity, Track, and Forward Speed deviations.
 */

import { useState, useEffect, useCallback } from "react";
import { trackRumAction } from "@/lib/datadog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import AbstractInset, { BaseInsetProps, InsetState } from "./AbstractInset";
import type {
  StormMetadataResponse,
  ScenarioDeviations,
} from "@/api/types/storms";

interface ScenarioControlsProps
  extends Omit<BaseInsetProps, "data" | "headers"> {
  metadata: StormMetadataResponse | null;
  values: ScenarioDeviations;
  selectedModel: string;
  onModelChange: (model: string) => void;
  onChange: (values: ScenarioDeviations) => void;
  onRunModel?: () => void;
  isLoading?: boolean;
  autoUpdate?: boolean;
}

/**
 * Custom range slider component styled to match the dashboard
 */
function RangeSlider({
  label,
  value,
  min,
  max,
  step,
  onChange,
  formatValue,
  disabled,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  formatValue?: (value: number) => string;
  disabled?: boolean;
}) {
  const displayValue = formatValue ? formatValue(value) : String(value);
  const percentage = ((value - min) / (max - min)) * 100;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-[11px] text-muted-foreground">{label}</Label>
        <span className="text-[11px] font-medium px-1.5 py-0 bg-primary/10 text-primary rounded">
          {displayValue}
        </span>
      </div>
      <div className="relative">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          disabled={disabled}
          className="w-full h-1.5 bg-muted rounded-lg appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed
            [&::-webkit-slider-thumb]:appearance-none
            [&::-webkit-slider-thumb]:h-3.5
            [&::-webkit-slider-thumb]:w-3.5
            [&::-webkit-slider-thumb]:rounded-full
            [&::-webkit-slider-thumb]:bg-primary
            [&::-webkit-slider-thumb]:cursor-pointer
            [&::-webkit-slider-thumb]:shadow-md
            [&::-webkit-slider-thumb]:border-2
            [&::-webkit-slider-thumb]:border-white
            [&::-moz-range-thumb]:h-3.5
            [&::-moz-range-thumb]:w-3.5
            [&::-moz-range-thumb]:rounded-full
            [&::-moz-range-thumb]:bg-primary
            [&::-moz-range-thumb]:cursor-pointer
            [&::-moz-range-thumb]:border-2
            [&::-moz-range-thumb]:border-white"
          style={{
            background: `linear-gradient(to right, hsl(var(--primary)) 0%, hsl(var(--primary)) ${percentage}%, hsl(var(--muted)) ${percentage}%, hsl(var(--muted)) 100%)`,
          }}
        />
        <div className="flex justify-between text-[9px] text-muted-foreground">
          <span>{formatValue ? formatValue(min) : min}</span>
          <span>{formatValue ? formatValue(max) : max}</span>
        </div>
      </div>
    </div>
  );
}

const ScenarioControls: React.FC<ScenarioControlsProps> = ({
  metadata,
  values,
  selectedModel,
  onModelChange,
  onChange,
  onRunModel,
  loading = false,
  isLoading = false,
  error = null,
  position = "top-left",
  width = "320px",
  height = "auto",
  title = "Scenario Controls",
  subtitle,
  isCollapsible = true,
  initiallyCollapsed = false,
  className,
  autoUpdate = true,
  panelMode = false,
}) => {
  const [localValues, setLocalValues] = useState<ScenarioDeviations>(values);
  const combinedLoading = loading || isLoading;

  // Update local values when external values change
  useEffect(() => {
    setLocalValues(values);
  }, [values]);

  // Handle slider change
  const handleSliderChange = useCallback(
    (key: keyof ScenarioDeviations, value: number) => {
      trackRumAction('scenario_deviation_changed', {
        deviation_type: key,
        new_value: value,
      });
      const newValues = { ...localValues, [key]: value };
      setLocalValues(newValues);

      if (autoUpdate) {
        onChange(newValues);
      }
    },
    [localValues, onChange, autoUpdate]
  );

  // Format intensity value (percentage)
  const formatIntensity = (value: number) => {
    if (value === 100) return "100%";
    return value > 100 ? `+${value - 100}%` : `-${100 - value}%`;
  };

  // Format track deviation (miles)
  const formatTrack = (value: number) => {
    if (value === 0) return "0";
    return value > 0 ? `${value} NE` : `${Math.abs(value)} SW`;
  };

  // Format speed value (percentage)
  const formatSpeed = (value: number) => {
    if (value === 100) return "100%";
    return value > 100 ? `+${value - 100}%` : `-${100 - value}%`;
  };

  // Scenario availability is per-model: any model that carries a deviation
  // grid (has_scenarios) drives the sliders — no longer hardcoded to OFCL.
  // Fall back to the top-level range for older API responses without models[].
  const modelMeta = metadata?.models?.find((m) => m.name === selectedModel);
  const hasScenarios = modelMeta?.has_scenarios ?? false;

  const intensityRange = modelMeta?.intensity_range ?? metadata?.intensity_range;
  const speedRange = modelMeta?.speed_range ?? metadata?.speed_range;
  const trackRange = modelMeta?.track_range ?? metadata?.track_range;

  // Compute effective ranges - use sensible defaults if API returns no range
  const intensityMin = intensityRange?.min ?? 75;
  const intensityMax = intensityRange?.max ?? 125;
  const intensityStep = intensityRange?.step ?? 5;
  const hasIntensityRange = intensityMin !== intensityMax;

  const speedMin = speedRange?.min ?? 75;
  const speedMax = speedRange?.max ?? 125;
  const speedStep = speedRange?.step ?? 5;
  const hasSpeedRange = speedMin !== speedMax;

  const trackMin = trackRange?.min ?? -150;
  const trackMax = trackRange?.max ?? 150;
  const trackStep = trackRange?.step ?? 25;
  const hasTrackRange = trackMin !== trackMax;

  // Use API ranges directly since we now have proper data
  const effectiveIntensityMin = intensityMin;
  const effectiveIntensityMax = intensityMax;
  const effectiveSpeedMin = speedMin;
  const effectiveSpeedMax = speedMax;
  const effectiveTrackMin = trackMin;
  const effectiveTrackMax = trackMax;

  return (
    <AbstractInset
      data={[]}
      loading={combinedLoading}
      error={error}
      position={position}
      width={width}
      height={height}
      title={title}
      subtitle={subtitle}
      isCollapsible={isCollapsible}
      initiallyCollapsed={initiallyCollapsed}
      className={className}
      panelMode={panelMode}
      containerId="scenario-controls-inset"
      infoTooltip={
        <div className="text-sm space-y-2">
          <p className="font-medium">Scenario Analysis</p>
          <ul className="text-xs space-y-1 list-disc pl-4">
            <li>
              <strong>Intensity</strong>: Adjust storm intensity (+/- %)
            </li>
            <li>
              <strong>Track</strong>: Shift storm path left/right (miles)
            </li>
            <li>
              <strong>Speed</strong>: Change forward movement speed (+/- %)
            </li>
          </ul>
          <p className="text-xs text-muted-foreground italic">
            Dynamic paths are available for models with pre-computed scenarios
          </p>
        </div>
      }
    >
      {(insetState: InsetState) => (
        <div className="p-2 space-y-2">
          {/* Weather Model Selector with Run Button */}
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">
              Select model
            </Label>
            <div className="flex gap-2">
              <Select
                value={selectedModel}
                onValueChange={onModelChange}
                disabled={combinedLoading}
              >
                <SelectTrigger className="flex-1 h-8">
                  <SelectValue placeholder="Select model" />
                </SelectTrigger>
                <SelectContent>
                  {metadata?.weather_models.map((model) => (
                    <SelectItem key={model} value={model}>
                      {model}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Run Model only shown for models with scenarios — others auto-update on select */}
              {hasScenarios && !autoUpdate && (
                <Button
                  onClick={() => {
                    onChange(localValues);
                    onRunModel?.();
                  }}
                  disabled={combinedLoading}
                  size="sm"
                  className="h-8 px-3"
                >
                  {combinedLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Run Model"
                  )}
                </Button>
              )}
            </div>
            {!hasScenarios && (
              <p className="text-xs text-muted-foreground italic">
                The {selectedModel} model has no pre-computed scenarios; sliders
                are disabled.
              </p>
            )}
          </div>

          {/* Intensity Slider — disabled when the model has no scenarios */}
          <RangeSlider
            label="Set storm intensity deviation (%)"
            value={localValues.intensity}
            min={effectiveIntensityMin}
            max={effectiveIntensityMax}
            step={intensityStep}
            onChange={(v) => handleSliderChange("intensity", v)}
            formatValue={formatIntensity}
            disabled={combinedLoading || !hasScenarios}
          />

          {/* Speed Slider — disabled when the model has no scenarios */}
          <RangeSlider
            label="Set storm forward speed deviation (%)"
            value={localValues.speed}
            min={effectiveSpeedMin}
            max={effectiveSpeedMax}
            step={speedStep}
            onChange={(v) => handleSliderChange("speed", v)}
            formatValue={formatSpeed}
            disabled={combinedLoading || !hasScenarios}
          />

          {/* Track Slider — only when the model has scenarios */}
          <RangeSlider
            label="Set path deviation (miles)"
            value={localValues.track}
            min={effectiveTrackMin}
            max={effectiveTrackMax}
            step={trackStep}
            onChange={(v) => handleSliderChange("track", v)}
            formatValue={formatTrack}
            disabled={combinedLoading || !hasScenarios}
          />

          {/* Info about available scenarios - only show if limited data */}
          {(!hasIntensityRange && !hasSpeedRange && !hasTrackRange) && (
            <p className="text-xs text-muted-foreground italic border-t pt-2">
              Note: Limited scenario data available. Slider adjustments may not have pre-computed results.
            </p>
          )}
        </div>
      )}
    </AbstractInset>
  );
};

export { ScenarioControls };
export type { ScenarioControlsProps };
